import React from "react"
import { styled } from "@mui/material/styles"
import {
  Avatar,
  Button,
  Divider,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Link,
  ListItemAvatar,
  ListItemText,
} from "@mui/material"
import { timeAgoInWords } from "../utils/stringUtils"
import ReplyCard from "./ReplyCard"
import ConfirmationPopover from "./ConfirmationPopover"

const CommentContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "isHighlighted",
})(({ isHighlighted, theme }) => ({
  backgroundColor: isHighlighted ? theme.palette.action.hover : "transparent",
  marginTop: "0px",
  padding: "5px 10px 0px",
  transition: "background-color 0.3s",
}))

const CommentUserHeaderGrid = styled(Grid)`
  display: flex;
  align-items: center;
`

const CommentAvatarWrapper = styled(Grid)`
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
`

const CommentAvatarLink = styled(Link)`
  text-decoration: none;
`

const CommentUserAvatar = styled(Avatar)`
  margin-left: 10px;
`

const CommentUserSlug = styled(ListItemText)`
  text-align: left;
  display: flex;
  align-items: center;
  &:hover {
    text-decoration: none;
  }
`

const CommentUserLink = styled(Link)`
  text-decoration: none;
  color: #000000;
  &:hover {
    text-decoration: underline;
  }
`

const CommentTimestamp = styled("span")`
  color: #c0c0c0;
  font-size: 13px;
  margin-left: 10px;
  &:hover {
    text-decoration: none;
  }
`

const CommentBody = styled(ListItemText)`
  word-wrap: break-word;
  overflow-wrap: break-word;
  margin-right: 10px;
  text-align: left;
`

const ReplyGrid = styled(Grid)`
  margin: 0px 5px 5px 70px;
`

const ReplyTextAreaContainer = styled("div")`
  width: 320px;
  margin-left: 70px;
`

const ReplyInputAvatar = styled(Avatar)`
  width: 24px;
  height: 24px;
`

const TextareaButton = styled(Button)`
  font-size: 12px;
  margin-bottom: 5px;
  color: #333333;
  border-color: #333333;

  &.MuiButton-contained {
    color: white;
  }
`

const SFIconButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  border-radius: 25%;
  &:hover {
    background-color: #80808026;
  }
  &:focus {
    background-color: transparent;
  }
  i {
    font-size: 12px;
  }
`

const ActionGridContainer = styled(Grid)`
  margin: 0px 0px 5px 54px;
`

const ActionGrid = styled(Grid)`
  display: flex;
  align-items: center;
`

const ReplyButton = styled(Button)`
  margin: 7px 0;
  font-size: 12px;
  color: #4f4f4f;
  font-weight: 600;

  &:hover {
    background-color: #80808026;
  }
  &:focus {
    background-color: transparent;
  }
`

const ActionIcon = styled("i")`
  font-size: 10px;
  padding: 0;
  color: #757575;
`

export default class CommentCard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isReply: false,
      showReplyForm: false,
      replyText: "",
      popoverAnchorEl: null,
      commentToDelete: null,
    }
    this.replyTextAreaRef = React.createRef()
  }

  componentDidMount() {
    const { comment } = this.props
    if (comment.parent_id) {
      this.setState({ isReply: true })
    }
  }

  toggleReplyForm = () => {
    this.setState(
      (prevState) => ({ showReplyForm: !prevState.showReplyForm }),
      () => {
        if (this.state.showReplyForm && this.replyTextAreaRef.current) {
          this.replyTextAreaRef.current.scrollIntoView({ behavior: "smooth" })
        }
      }
    )
  }

  handleTextChange = (event) => {
    this.setState({ replyText: event.target.value })
  }

  handleReplyButtonClick = async () => {
    const { replyText } = this.state
    const { comment, handleSubmitComment } = this.props
    const trimmedReply = replyText.trim()
    if (trimmedReply) {
      await handleSubmitComment(replyText, comment.id)
      this.setState({ replyText: "", showReplyForm: false })
    }
  }

  handleReplyKeyDown = async (event) => {
    if (event.shiftKey && event.key === "Enter") {
      event.preventDefault()
      const { replyText } = this.state
      const trimmedReply = replyText.trim()
      if (trimmedReply.length > 0) {
        await this.handleReplySubmit(replyText)
        this.setState({ replyText: "" })
      }
    }
  }

  handleReplySubmit = async (body) => {
    const { comment, handleSubmitComment } = this.props
    await handleSubmitComment(body, comment.id)
    this.setState({ replyText: "", showReplyForm: false })
  }

  openPopover = (event, commentId) => {
    this.setState({
      popoverAnchorEl: event.currentTarget,
      commentToDelete: commentId,
    })
  }

  closePopover = () => {
    this.setState({ popoverAnchorEl: null, commentToDelete: null })
  }

  confirmDelete = () => {
    const { handleDeleteComment } = this.props
    const { commentToDelete } = this.state
    if (commentToDelete) {
      handleDeleteComment(commentToDelete)
    }
    this.closePopover()
  }

  renderReplies() {
    const {
      comment,
      currentUser,
      handleDeleteComment,
      handleSubmitComment,
      highlightedCommentId,
    } = this.props

    return comment.replies.map((reply) => (
      <ReplyGrid item xs={12} key={reply.id} id={`comment-${reply.id}`}>
        <ReplyCard
          comment={comment}
          reply={reply}
          currentUser={currentUser}
          handleDeleteComment={(replyId) =>
            handleDeleteComment(replyId, comment.id)
          }
          isHighlighted={highlightedCommentId === reply.id.toString()}
          handleSubmitComment={handleSubmitComment}
        />
      </ReplyGrid>
    ))
  }

  renderReplyTextArea() {
    const { currentUserAvatar } = this.props
    const { replyText } = this.state
    const isReplyButtonDisabled = Boolean(!replyText)

    return (
      <ReplyTextAreaContainer>
        <Grid item xs={12}>
          <Input
            placeholder="Add your reply!"
            margin="none"
            size="small"
            fullWidth
            disableUnderline
            onChange={this.handleTextChange}
            onKeyDown={this.handleReplyKeyDown}
            startAdornment={
              <InputAdornment position="start">
                <ReplyInputAvatar
                  src={currentUserAvatar}
                  alt={currentUserAvatar}
                />
              </InputAdornment>
            }
          />
        </Grid>
        <Grid container justifyContent="flex-end" spacing={2}>
          <Grid item xs={3}>
            <TextareaButton
              onClick={this.toggleReplyForm}
              size="small"
              variant="text"
            >
              Cancel
            </TextareaButton>
          </Grid>
          <Grid item xs={3}>
            <TextareaButton
              onClick={this.handleReplyButtonClick}
              size="small"
              variant="contained"
              color="primary"
              disabled={isReplyButtonDisabled}
            >
              Reply
            </TextareaButton>
          </Grid>
        </Grid>
      </ReplyTextAreaContainer>
    )
  }

  render() {
    const { showReplyForm, popoverAnchorEl } = this.state
    const { comment, currentUser, highlightedCommentId } = this.props
    const isPopoverOpen = Boolean(popoverAnchorEl)
    const isHighlighted = highlightedCommentId === comment.id.toString()

    return (
      <div>
        <CommentContainer
          container
          spacing={0.5}
          isHighlighted={isHighlighted}
          ref={this.replyTextAreaRef}
        >
          <CommentAvatarWrapper item xs={2}>
            <ListItemAvatar>
              <CommentAvatarLink href={comment.commentor_user_url}>
                <CommentUserAvatar
                  alt={comment.commentor_user_name}
                  src={comment.commentor_user_avatar}
                />
              </CommentAvatarLink>
            </ListItemAvatar>
          </CommentAvatarWrapper>
          <Grid item xs={10}>
            <CommentUserHeaderGrid item xs={12}>
              <CommentUserSlug>
                <CommentUserLink href={comment.commentor_user_url}>
                  <strong>{comment.commentor_user_name}</strong>
                </CommentUserLink>
                <CommentTimestamp>
                  {timeAgoInWords(comment.created_at)}
                </CommentTimestamp>
              </CommentUserSlug>
            </CommentUserHeaderGrid>
            <Grid item xs={12}>
              <CommentBody>{comment.body}</CommentBody>
            </Grid>
          </Grid>
          <ActionGridContainer container>
            <ActionGrid item xs={5}>
              {currentUser && (
                <div>
                  <ReplyButton
                    onClick={this.toggleReplyForm}
                    variant="text"
                    size="small"
                    color="secondary"
                  >
                    Reply
                  </ReplyButton>
                </div>
              )}
              {(currentUser?.slug === comment.commentor_slug ||
                currentUser?.can_moderate === true) && (
                <>
                  <SFIconButton
                    size="small"
                    onClick={(event) => this.openPopover(event, comment.id)}
                    aria-label="delete"
                  >
                    <ActionIcon className="far fa-trash-can" />
                  </SFIconButton>
                  <ConfirmationPopover
                    anchorEl={popoverAnchorEl}
                    open={isPopoverOpen}
                    onClose={this.closePopover}
                    onConfirm={this.confirmDelete}
                    message="Are you sure you want to delete this comment?"
                  />
                </>
              )}
            </ActionGrid>
          </ActionGridContainer>
        </CommentContainer>
        {showReplyForm && this.renderReplyTextArea()}
        {this.renderReplies()}
        <Divider />
      </div>
    )
  }
}
